/**
 * PointagePage — pointage matin de la présence (cantine + périscolaire), lot 4.
 *
 * Le staff (agent terrain, manager, élus) sélectionne une date, voit la liste
 * nominative des enfants attendus et marque présent/absent/absent justifié.
 * Boutons de téléchargement : feuille cuisine (cantine) et feuille de présence
 * (périscolaire) en PDF A4.
 */

import { useEffect, useState } from 'react';
import { EtatRepasAgentsPanel } from '@/components/famille/EtatRepasAgentsPanel';
import { JustificatifsAbsencePanel } from '@/components/famille/JustificatifsAbsencePanel';
import { Download, UtensilsCrossed, Sparkles } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Button, Input, Field, Alert, Badge } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import { RESERVATION_STATUTS } from '@roronoa-collectivites/shared-types';
import type { CantineListeJour, PeriscolaireListeJour, ReservationStatut } from '@roronoa-collectivites/shared-types';

type Onglet = 'cantine' | 'periscolaire' | 'alsh' | 'adultes';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

const STATUT_COURT: Record<ReservationStatut, string> = {
  reserve: 'Réservé', present: 'Présent', absent: 'Absent', absent_justifie: 'Justifié',
};

export function PointagePage() {
  const [onglet, setOnglet] = useState<Onglet>('cantine');
  // Items 12.5/12.6 — la feuille SÉPARÉE des adultes : le cuisinier compte ses
  // couverts adultes à part, c'était la demande explicite de la mairie.
  const [adultes, setAdultes] = useState<{ total: number; lignes: Array<{ repasId: string; nomComplet: string; categorie: string; statut: string }> } | null>(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [cantine, setCantine] = useState<CantineListeJour | null>(null);
  const [peri, setPeri] = useState<PeriscolaireListeJour | null>(null);
  const [alsh, setAlsh] = useState<{ dateService: string; lignes: { reservationId: string; enfantId: string; prenom: string; nom: string; classe: string | null; ecole: string | null; formule: 'journee' | 'matin' | 'apres_midi'; statut: ReservationStatut; heureEntree: string | null; heureSortie: string | null; repas: boolean; mangeCantine: boolean; alshOuverture: string | null; alshFermeture: string | null }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [modePeri, setModePeri] = useState<'forfait' | 'temps'>('forfait');
  const [modeAlsh, setModeAlsh] = useState<'forfait' | 'temps'>('forfait');

  useEffect(() => {
    let off = false;
    (async () => {
      try { const p = await api.scolaire.getParametres(); if (!off) { setModePeri(p.modeFacturationPeriscolaire); setModeAlsh(p.modeFacturationAlsh); } } catch { /* défaut forfait */ }
    })();
    return () => { off = true; };
  }, []);

  async function load() {
    setLoading(true); setError(null);
    try {
      if (onglet === 'cantine') setCantine(await api.scolaire.getListeCantine(date));
      else if (onglet === 'alsh') setAlsh(await api.scolaire.getListeAlsh(date));
      else if (onglet === 'adultes') setAdultes(await api.scolaire.adultes.listeJour(date));
      else setPeri(await api.scolaire.periscolaire.getListe(date));
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [onglet, date]);

  /** Pointer un adulte : même geste que pour un enfant, feuille séparée. */
  async function pointerAdulteLigne(repasId: string, statut: ReservationStatut): Promise<void> {
    await api.scolaire.adultes.pointer(date, [{ repasId, statut }]);
    setAdultes(await api.scolaire.adultes.listeJour(date));
  }

  async function pointerCantine(reservationId: string, statut: ReservationStatut) {
    try {
      await api.scolaire.pointer({ reservationId, statut });
      setCantine((prev) => prev && { ...prev, lignes: prev.lignes.map((l) => l.reservationId === reservationId ? { ...l, statut } : l) });
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de pointage'); }
  }
  async function pointerPeri(reservationId: string, statut: ReservationStatut) {
    try {
      await api.scolaire.periscolaire.pointer({ reservationId, statut });
      setPeri((prev) => prev && {
        ...prev,
        matin: prev.matin.map((l) => l.reservationId === reservationId ? { ...l, statut } : l),
        soir: prev.soir.map((l) => l.reservationId === reservationId ? { ...l, statut } : l),
      });
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de pointage'); }
  }

  async function pointerAlshLigne(reservationId: string, statut: ReservationStatut) {
    try {
      await api.scolaire.pointerAlsh({ reservationId, statut });
      setAlsh((prev) => prev && { ...prev, lignes: prev.lignes.map((l) => l.reservationId === reservationId ? { ...l, statut } : l) });
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de pointage'); }
  }

  async function horodaterAlshLigne(reservationId: string, sens: 'entree' | 'sortie', heure?: string) {
    try {
      await api.scolaire.horodaterAlsh({ reservationId, sens, heure });
      const champ = sens === 'entree' ? 'heureEntree' : 'heureSortie';
      setAlsh((prev) => prev && { ...prev, lignes: prev.lignes.map((l) => l.reservationId === reservationId ? { ...l, [champ]: heure ?? new Date().toISOString(), statut: 'present' as ReservationStatut } : l) });
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur d’horodatage'); }
  }

  async function setRepasAlshLigne(reservationId: string, repas: boolean) {
    try {
      await api.scolaire.setRepasAlsh({ reservationId, repas });
      setAlsh((prev) => prev && { ...prev, lignes: prev.lignes.map((l) => l.reservationId === reservationId ? { ...l, repas } : l) });
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function horodaterPeri(reservationId: string, sens: 'entree' | 'sortie', heure?: string) {
    try {
      const r = await api.scolaire.periscolaire.horodater({ reservationId, sens, heure });
      const champ = sens === 'entree' ? 'heureEntree' : 'heureSortie';
      setPeri((prev) => prev && {
        ...prev,
        matin: prev.matin.map((l) => l.reservationId === reservationId ? { ...l, [champ]: heure ?? new Date().toISOString(), statut: sens === 'entree' ? 'present' : l.statut } : l),
        soir: prev.soir.map((l) => l.reservationId === reservationId ? { ...l, [champ]: heure ?? new Date().toISOString(), statut: sens === 'entree' ? 'present' : l.statut } : l),
      });
      void r;
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur d\'horodatage'); }
  }

  async function telecharger() {
    setPdfBusy(true); setError(null);
    try {
      // Aiguillage EXPLICITE par onglet. Le `else` attrape-tout d'origine
      // envoyait l'onglet « adultes » (ajouté en mig 250) sur la feuille
      // PÉRISCOLAIRE — bug remonté par la mairie le 27/07.
      switch (onglet) {
        case 'cantine':
          downloadBlob(await api.scolaire.feuilleCuisinePdfBlob(date), `feuille-cuisine-${date}.pdf`);
          break;
        case 'alsh':
          downloadBlob(await api.scolaire.feuilleAlshPdfBlob(date), `feuille-alsh-${date}.pdf`);
          break;
        case 'adultes':
          downloadBlob(await api.scolaire.feuilleAdultesPdfBlob(date), `feuille-adultes-${date}.pdf`);
          break;
        case 'periscolaire':
          downloadBlob(await api.scolaire.periscolaire.feuillePdfBlob(date), `feuille-periscolaire-${date}.pdf`);
          break;
      }
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors du téléchargement'); }
    finally { setPdfBusy(false); }
  }

  return (
    <ScolaireLayout activeTab="pointage">
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Pointage du jour</h1>
            <p className="mt-1 text-sm text-ink-muted">Marquez les présences et éditez les feuilles.</p>
          </div>
          <Button variant="ghost" loading={pdfBusy} onClick={telecharger}>
            <Download className="h-4 w-4" aria-hidden="true" /> {onglet === 'cantine' ? 'Feuille cuisine' : onglet === 'adultes' ? 'Feuille adultes' : 'Feuille présence'}
          </Button>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setOnglet('cantine')}
            className={['inline-flex h-9 items-center gap-1.5 rounded-r-pill px-4 text-sm font-medium transition-all', onglet === 'cantine' ? 'bg-cantine text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70'].join(' ')}>
            <UtensilsCrossed className="h-4 w-4" /> Cantine
          </button>
          <button type="button" onClick={() => setOnglet('periscolaire')}
            className={['inline-flex h-9 items-center gap-1.5 rounded-r-pill px-4 text-sm font-medium transition-all', onglet === 'periscolaire' ? 'bg-cantine text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70'].join(' ')}>
            <Sparkles className="h-4 w-4" /> Périscolaire
          </button>
          <button type="button" onClick={() => setOnglet('alsh')}
            className={['inline-flex h-9 items-center gap-1.5 rounded-r-pill px-4 text-sm font-medium transition-all', onglet === 'alsh' ? 'bg-cantine text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70'].join(' ')}>
            <Sparkles className="h-4 w-4" /> ALSH
          </button>
          <button type="button" onClick={() => setOnglet('adultes')}
            className={['inline-flex h-9 items-center gap-1.5 rounded-r-pill px-4 text-sm font-medium transition-all', onglet === 'adultes' ? 'bg-cantine text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70'].join(' ')}>
            <UtensilsCrossed className="h-4 w-4" /> Adultes
          </button>
          <div className="ml-auto w-44">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Date du pointage" />
          </div>
        </div>

        {onglet === 'adultes' && (
          <div className="mb-4">
            <EtatRepasAgentsPanel peutTransmettre />
          </div>
        )}

        {/* Item 12.3 — la file des justificatifs vit ici : c'est au moment du
            pointage qu'on constate l'absence et qu'on traite le document. */}
        <div className="mb-4">
          <JustificatifsAbsencePanel />
        </div>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : onglet === 'cantine' ? (
          <PointageListe
            lignes={(cantine?.lignes ?? []).map((l) => ({ id: l.reservationId, nom: `${l.prenom} ${l.nom}`, sous: l.classe ?? l.ecole ?? '', statut: l.statut, pai: l.paiStatut === 'a_jour' || l.allergenes.length > 0 }))}
            onPoint={pointerCantine}
            vide="Aucun enfant inscrit à la cantine ce jour."
          />
        ) : onglet === 'adultes' ? (
          /* Items 12.5/12.6 — la feuille SÉPARÉE des adultes. Même composant
             de pointage que pour les enfants : c'est le même geste, au même
             moment, sur une autre population. */
          <PointageListe
            lignes={(adultes?.lignes ?? []).map((l) => ({
              id: l.repasId,
              nom: l.nomComplet,
              sous: ({ agent: 'Agent', enseignant: 'Enseignant', elu: 'Élu', exterieur: 'Extérieur' } as Record<string, string>)[l.categorie] ?? l.categorie,
              statut: l.statut as ReservationStatut,
              pai: false,
            }))}
            onPoint={pointerAdulteLigne}
            vide="Aucun adulte inscrit à la cantine ce jour."
          />
        ) : onglet === 'alsh' ? (
          <PointageListe
            dateService={date}
            lignes={(alsh?.lignes ?? []).map((l) => ({
              id: l.reservationId,
              nom: `${l.prenom} ${l.nom}`,
              sous: [l.ecole, l.formule === 'journee' ? 'Journée' : l.formule === 'matin' ? 'Matin' : 'Après-midi'].filter(Boolean).join(' · '),
              statut: l.statut,
              pai: false,
              heureEntree: l.heureEntree,
              heureSortie: l.heureSortie,
              defautEntree: l.alshOuverture ?? '08:00',
              defautSortie: l.alshFermeture ?? '18:00',
              repas: l.repas,
              repasSuggere: l.mangeCantine,
            }))}
            onPoint={pointerAlshLigne}
            modeTemps={modeAlsh === 'temps'}
            onHorodater={horodaterAlshLigne}
            onToggleRepas={setRepasAlshLigne}
            vide="Aucun enfant inscrit à l'ALSH ce jour."
          />
        ) : (
          <>
            <h2 className="mb-2 mt-2 font-display text-sm font-semibold text-ink">Matin</h2>
            <PointageListe
              dateService={date}
              lignes={(peri?.matin ?? []).map((l) => ({ id: l.reservationId, nom: `${l.prenom} ${l.nom}`, sous: l.classe ?? '', statut: l.statut, pai: false, heureEntree: l.heureEntree, heureSortie: l.heureSortie, defautEntree: l.garderieOuvertureMatin ?? '07:30', defautSortie: l.horaireOuverture ?? '08:30' }))}
              onPoint={pointerPeri}
              modeTemps={modePeri === 'temps'}
              onHorodater={horodaterPeri}
              vide="Aucun enfant le matin."
            />
            <h2 className="mb-2 mt-6 font-display text-sm font-semibold text-ink">Soir</h2>
            <PointageListe
              dateService={date}
              lignes={(peri?.soir ?? []).map((l) => ({ id: l.reservationId, nom: `${l.prenom} ${l.nom}`, sous: l.classe ?? '', statut: l.statut, pai: false, heureEntree: l.heureEntree, heureSortie: l.heureSortie, defautEntree: l.horaireFinJournee ?? '16:30' }))}
              onPoint={pointerPeri}
              modeTemps={modePeri === 'temps'}
              onHorodater={horodaterPeri}
              vide="Aucun enfant le soir."
            />
          </>
        )}
      </main>
    </ScolaireLayout>
  );
}

function PointageListe({
  lignes, onPoint, vide, modeTemps, onHorodater, dateService, onToggleRepas,
}: {
  lignes: { id: string; nom: string; sous: string; statut: ReservationStatut; pai: boolean; heureEntree?: string | null; heureSortie?: string | null; defautEntree?: string; defautSortie?: string; repas?: boolean; repasSuggere?: boolean }[];
  onPoint: (id: string, statut: ReservationStatut) => void;
  vide: string;
  modeTemps?: boolean;
  onHorodater?: (id: string, sens: 'entree' | 'sortie', heure?: string) => void;
  dateService?: string;
  onToggleRepas?: (id: string, repas: boolean) => void;
}) {
  const hhmm = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  // Convertit une heure HH:MM en ISO sur la DATE POINTÉE (pas aujourd'hui).
  const toIso = (hm: string) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(hm.trim());
    if (!m) return undefined;
    const base = dateService && /^\d{4}-\d{2}-\d{2}$/.test(dateService) ? new Date(`${dateService}T00:00:00`) : new Date();
    base.setHours(parseInt(m[1] ?? '0', 10), parseInt(m[2] ?? '0', 10), 0, 0);
    return base.toISOString();
  };
  if (lignes.length === 0) return <Card><CardBody><p className="text-sm text-ink-muted">{vide}</p></CardBody></Card>;
  return (
    <ul className="space-y-2">
      {lignes.map((l) => (
        <li key={l.id}>
          <Card>
            <CardBody className="py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink">{l.nom}</span>
                    {l.pai && <Badge>PAI / allergie</Badge>}
                  </div>
                  {l.sous && <p className="text-xs text-ink-subtle">{l.sous}</p>}
                </div>
                <span className="shrink-0 text-xs text-ink-muted">{STATUT_COURT[l.statut]}</span>
              </div>

              {modeTemps && onHorodater ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => onHorodater(l.id, 'entree', l.defautEntree ? toIso(l.defautEntree) : undefined)}
                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                      l.heureEntree ? 'bg-success text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                    Arrivée{l.heureEntree ? ` ✓` : ''}
                  </button>
                  <input type="time" value={hhmm(l.heureEntree)} aria-label="Heure d'arrivée"
                    onChange={(e) => { const iso = toIso(e.target.value); if (iso) onHorodater(l.id, 'entree', iso); }}
                    className="h-8 rounded-r-md border border-border bg-surface px-2 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine" />
                  <span className="text-ink-subtle">→</span>
                  <button type="button" onClick={() => onHorodater(l.id, 'sortie', l.defautSortie ? toIso(l.defautSortie) : undefined)}
                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                      l.heureSortie ? 'bg-danger text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                    Départ{l.heureSortie ? ` ✓` : ''}
                  </button>
                  <input type="time" value={hhmm(l.heureSortie)} aria-label="Heure de départ"
                    onChange={(e) => { const iso = toIso(e.target.value); if (iso) onHorodater(l.id, 'sortie', iso); }}
                    className="h-8 rounded-r-md border border-border bg-surface px-2 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine" />
                  <button type="button" onClick={() => onPoint(l.id, 'absent')}
                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                      l.statut === 'absent' ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                    Absent
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {RESERVATION_STATUTS.filter((s) => s.value !== 'reserve').map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => onPoint(l.id, s.value)}
                      className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                        l.statut === s.value ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}

              {onToggleRepas && (
                <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-xs text-ink-muted">
                  <input type="checkbox" checked={l.repas ?? false}
                    onChange={(e) => onToggleRepas(l.id, e.target.checked)}
                    className="h-4 w-4 rounded border-border text-cantine focus-visible:ring-2 focus-visible:ring-cantine" />
                  Repas (déjeuner)
                  {l.repasSuggere && !l.repas && (
                    <span className="rounded-r-pill bg-cantine/10 px-2 py-0.5 text-2xs font-medium text-cantine-fg">inscrit cantine ce jour</span>
                  )}
                </label>
              )}
            </CardBody>
          </Card>
        </li>
      ))}
    </ul>
  );
}
